import { useEffect, useMemo, useState } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { useAuth } from 'react-oidc-context';
import OrderListFilters from 'src/views/uiflow/orders/components/OrderListFilters';
import { getSalesOrderItems, getSalesOrders } from 'src/services/salesorder';
import { endOfDay, format, startOfDay } from 'date-fns';
import VigoFilteredList, { filter, hideColumnByProduct } from 'src/@vigo/vigo-filteredlist';
import DefaultGridState from '../../../../sharedgridstates/orders/dashboard/defaultDashboardState.json';
import DefaultDeliveryGridState from '../../../../sharedgridstates/orders/dashboard/defaultDashboardDeliveryState.json';
import DefaultCollectionGridState from '../../../../sharedgridstates/orders/dashboard/defaultDashboardCollectionState.json';
import { DataTableAction } from 'src/@vigo/vigo-datatable/dataTable-types';
import { useSettings } from 'src/@core/hooks/useSettings';
import { MapClockOutline, PencilOutline } from 'mdi-material-ui';
import { pushNavigationStack } from 'src/store/slices/uiFlowSlice';
import { useRouter } from 'next/router';
import { useAppDispatch } from 'src/store/baseHooks';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { SalesOrderItemDto, SalesOrderListDto } from 'src/models/salesorder';
import { VigoOrderStatusPanelProps } from 'src/@vigo/vigo-orderstatuspanel';
import { useAdditionalDataTransferObject } from 'src/@core/hooks/useAdditionalDataTransferObject';
import StatusHistoryDrawer from './StatusHistoryDrawer';
import styles from './orderList.module.css';
import { useNewGridApiRefContext } from 'src/@core/context/newGridRefContext';

const filters: filter[] = [
  {
    name: 'Delivery Date',
    property: 'deliveryDateTime',
    value: 0,
    compareType: 'datetime',
    defaultGridState: DefaultDeliveryGridState
  },
  {
    name: 'Collection Date',
    property: 'collectionDateTime',
    value: 1,
    compareType: 'datetime',
    defaultGridState: DefaultCollectionGridState
  },
  {
    name: 'Entered Date',
    property: 'orderDate',
    value: 3,
    compareType: 'datetime',
    defaultGridState: DefaultGridState
  }
];

const OrdersListWidget = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [dates, setDates] = useState<string>('');
  const [parentFilter, setParentFilter] = useState<string>('');

  const prod = useGetProductLevel();
  const { t } = useVigoTranslation();

  const { settings } = useSettings();

  const grid = useNewGridApiRefContext();

  // get the logged in customer so that if we are a portal user we can set the partner Id
  const auth = useAuth();
  const user = auth.user;
  const partnerId = (user?.profile?.partnerId as string) ?? '';
  const theme = useTheme();
  const router = useRouter();
  const uiFlowDispatch = useAppDispatch();

  const { set } = useAdditionalDataTransferObject();

  const path = router.asPath;
  const isDashboardBuilder = path.includes('DashboardBuilder');

  const hideColumnByProducts: hideColumnByProduct[] = useMemo(
    () => [
      {
        product: Products.MyPortal,
        property: [
          'accountCode',
          'price',
          'itemsReadyForCollection',
          'itemsLoadedToVehicle',
          'itemsNotSending',
          'itemsNotScanned'
        ]
      },
      {
        product: Products.Flow,
        property: ['itemsReadyForCollection', 'itemsLoadedToVehicle', 'itemsNotSending', 'itemsNotScanned']
      }
    ],
    []
  );

  const customActions: DataTableAction[] = useMemo(() => {
    const actionArray = [];

    if (!settings.showUATRecords) {
      actionArray.push({
        id: '43123',
        icon: <PencilOutline />,
        text: 'Edit',
        alwaysShow: true,
        clickHandler: (record: any) => {
          uiFlowDispatch(pushNavigationStack(router.asPath));
          router.push(`/uiflow/consignment/${record.id}?drillDown=1`);
        }
      } as DataTableAction);

      actionArray.push({
        id: '12389',
        icon: <MapClockOutline />,
        text: t('Tracking'),
        alwaysShow: true,
        clickHandler: async (record: SalesOrderListDto) => {
          const orderItems: SalesOrderItemDto[] = (
            await getSalesOrderItems({ filter: `salesOrderId eq guid'${record.id}'` })
          ).data;
          const panelData: VigoOrderStatusPanelProps = {
            orderId: record.id ?? '',
            partnerId: record.ownerPartnerId ?? '',
            orderItems: orderItems.map(item => ({ id: item.id, itemNo: item.itemNo })),
            useOverride: false,
            isDelivered: record.latestStatus === 'Delivered' ? true : false,
            isDeleted: record.isDeleted
          };
          set('statusPanelProps', panelData);
          setOpen(true);
          grid.state.gridApiRef?.current?.instance()?.deselectAll();
          grid.state.gridApiRef?.current?.instance()?.selectRows([record.id ?? ''], true);
        }
      } as DataTableAction);
    }

    return actionArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.showUATRecords, grid]);

  useEffect(() => {
    /**
     * Check if dashboardDateStrings entries are empty, if so created dates for today.
     * This is to prevent the dashboard from breaking if there is no datarange availabele (dashboard builder for example).
     */
    if (!dashboardDateStrings[0]?.[0]) {
      dashboardDateStrings[0] = [`ge datetime'${format(startOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss')}' and`];
    }

    if (!dashboardDateStrings[1]?.[0]) {
      dashboardDateStrings[1] = [`le datetime'${format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss')}'`];
    }

    const newDates =
      new Date(dashboardDateStrings[0].toString().split("'")[1]).toDateString() +
      ' - ' +
      new Date(dashboardDateStrings[1].toString().split("'")[1]).toDateString();

    setDates(newDates);
  }, [dashboardDateStrings]);

  useEffect(() => {
    setParentFilter(prod === Products.MyPortal ? " customerId eq guid'" + partnerId + "'`}" : '');
  }, [prod, partnerId]);

  return (
    <>
      <Stack
        className={styles.widget_container}
        sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e' }}>
        <Box
          className={styles.widget_content_container}
          sx={{ backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e' }}>
          <Box
            sx={{
              marginTop: '-5px',
              width: '100%',
              height: '100%',
              backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e'
            }}>
            <VigoFilteredList
              multiSelect
              columnFilters={filters}
              listQueryFn={isDashboardBuilder ? () => Promise.resolve([]) : getSalesOrders}
              listFilters={OrderListFilters}
              showTracking={true}
              isItemView={false}
              showFilters={false}
              contained={true}
              showSearch={false}
              showPrint={false}
              showExport={false}
              hideFooter={false}
              showFilterPanel={false}
              customEdit={true}
              showColumnPicker={false}
              showLayoutReset={false}
              parentFilter={parentFilter}
              filterOverrideColumn={orderByValue}
              filterOverrideDateRange={dates}
              controlname="dashboard-orderlist"
              disableEffects={true}
              hideHeader={true}
              customActions={customActions}
              enableStatusGroupsQuery
              hasUATRecords
              hideColumnByProduct={hideColumnByProducts}
            />
          </Box>
        </Box>
      </Stack>

      <StatusHistoryDrawer open={open} setOpen={setOpen} />
    </>
  );
};

export default OrdersListWidget;
